import { AuthenticationService } from '@shared/authentication';
import { inject, Injectable } from '@angular/core';
import { LayersTypes } from '@app/core/shared/enums/field-view-layer.types';
import { environment } from '@environment/environment';
import ImageTile from 'ol/ImageTile';
import TileLayer from 'ol/layer/Tile';
import { XYZ } from 'ol/source';
import { switchMap, take } from 'rxjs/operators';
import { FieldViewOpenWeatherBaseMapLayer } from '../models/field-view';

@Injectable({
  providedIn: 'root'
})
export class OpenWeatherBaseMapLayerService {
  private readonly authService = inject(AuthenticationService);

  public openWeatherBaseMapLayers: FieldViewOpenWeatherBaseMapLayer[] = [
    { formLabel: null, layer: null },
    { formLabel: 'Clouds', layer: 'clouds_new' },
    { formLabel: 'Precipitation', layer: 'precipitation_new' },
    { formLabel: 'Sea level Pressure', layer: 'pressure_new' },
    { formLabel: 'Temperature', layer: 'temp_new' },
    { formLabel: 'Wind speed', layer: 'wind_new' }
  ];

  public weatherLayer = new TileLayer({
    properties: { layerType: LayersTypes.WEATHER_MAP_LAYER },
    source: new XYZ({
      url: '',
      tileLoadFunction: (imageTile, src) => {
        this.authService.accessToken$
          .pipe(
            take(1),
            switchMap(token => {
              const xhr = new XMLHttpRequest();
              xhr.open('GET', src);
              xhr.setRequestHeader('Authorization', `Bearer ${token}`);
              xhr.responseType = 'blob';
              xhr.onload = () => {
                if (xhr.status === 200) {
                  const imageElement = (imageTile as ImageTile).getImage();
                  if (imageElement instanceof HTMLImageElement) {
                    imageElement.src = URL.createObjectURL(xhr.response);
                  }
                }
              };
              xhr.send();
              return [];
            })
          )
          .subscribe();
      }
    })
  });

  public createWeatherMapLayer(layer: string, unixTimestamp: number) {
    this.updateLegend(layer);

    if (layer === null) {
      this.weatherLayer.setVisible(false);
      return;
    }

    this.weatherLayer
      .getSource()
      ?.setUrl(`${environment.openWeatherUrl}${layer}/{z}/{x}/{y}.png?date=${unixTimestamp}`);
    this.weatherLayer.setVisible(true);
  }

  private updateLegend(layerType: string) {
    // Hex values obtained from inspecting open weather live demo
    const legendData: Record<string, { description: string; colorScale: string; values: string[] }> = {
      clouds_new: {
        description: 'Cloudiness (%)',
        colorScale:
          'linear-gradient(to right, #f7f7ff00 0%, #fbf7ff00 10%, #f4f8ff1a 20%, #f0f9ff33 30%, #ddfaff66 40%, #e0e0e0e6 50%, #e0e0e0c2 60%, #e4e4e4e6 70%, #e8e8e8e6 80%, #d6d5d5 90%, #d2d2d2 95%, #b7b7b7 100%)',
        values: ['0', '25', '50', '75', '100']
      },
      precipitation_new: {
        description: 'Precipitation intensity (mm/h)',
        colorScale: 'linear-gradient(to right, #0000ff00, #0000ff)',
        values: ['0', '0.5', '1', '2', '4', '6', '7', '10', '12', '14', '16', '24', '32', '60']
      },
      pressure_new: {
        description: 'Sea level pressure (hPa)',
        colorScale:
          'linear-gradient(to right, #0073ff 0%, #00aaff 8.35059%, #4bd0d6 24.9192%, #8de7c7 41.4879%, #b0f720 49.7722%, #f0b800 58.0565%, #fb5515 74.6251%, #f3363b 91.1938%, #c60000 100%)',
        values: ['950', '980', '1010', '1040', '1070']
      },
      temp_new: {
        description: 'Temperature (°C)',
        colorScale:
          'linear-gradient(to right, #9f55b5 0%, #2c6abb 8.75%, #528bd5 12.5%, #67a3de 18.75%, #8ecaf0 25%, #9bd5f4 31.25%, #ace1fd 37.5%, #c2eaff 43.75%, #ffffd0 50%, #fef8ae 56.25%, #fee892 62.5%, #fee270 68.75%, #fdd461 75%, #f4a85e 82.5%, #f48159 87.5%, #f46859 93.75%, #f44c49 100%)',
        values: ['-40', '-20', '0', '20', '40']
      },
      wind_new: {
        description: 'Wind speed (m/s).',
        colorScale:
          'linear-gradient(to left, #9e80b1, #744cac, #a47baa, #aa80b1d6, #b080b1b5, #aa80b18c, #aa80b170, #ffff0000)',
        values: ['0', '2', '3', '6', '12', '25', '50', '100']
      }
    };

    const selectedLegend = legendData[layerType];
    const legend = document.getElementById('legend') as HTMLElement;

    if (selectedLegend) {
      legend.style.display = 'block';
      legend.innerHTML = `
      <p><strong>${selectedLegend.description}</strong></p>
      <div class="gradient-container">
        <div class="gradient-bar" style="background: ${selectedLegend.colorScale};"></div>
        <div class="gradient-scale">
          ${selectedLegend.values.map(value => `<span>${value}</span>`).join('')}
        </div>
      </div>
    `;
    } else {
      legend.style.display = 'none';
    }
  }
}
